import React, { useState, useEffect, useRef, useMemo } from 'react';
import Slider from 'react-slick';
import { Helmet } from 'react-helmet-async';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './ImpactInsightsPage.scss';
import { API_BASE } from '../../config/environments/api';
import { SITE_BASE_URL } from '../../config/environments/api';

// Hook to animate number increase
const useAnimatedNumber = (formattedValue, shouldAnimate = true, animationSpeed = 15) => {
  const [displayValue, setDisplayValue] = useState(formattedValue);
  const previousValueRef = useRef(formattedValue);
  const animationRef = useRef(null);

  useEffect(() => {
    // Store the original formatted value to use as template
    const originalValue = formattedValue;
    
    // Parse numeric value from formatted string
    const parseNumericValue = (val) => {
      if (val === null || val === undefined || val === 'NA' || val === '' || val === '0') return 0;
      
      // Remove commas, spaces, currency symbols, and extract number part
      const cleaned = String(val).replace(/[,\s$€]/g, '');
      
      // Handle percentage, million (M), billion (B), etc.
      let multiplier = 1;
      let numStr = cleaned;
      
      if (cleaned.includes('M') && !cleaned.includes('(M)')) {
        multiplier = 1000000;
        numStr = cleaned.replace('M', '');
      } else if (cleaned.includes('B')) {
        multiplier = 1000000000;
        numStr = cleaned.replace('B', '');
      } else if (cleaned.includes('K')) {
        multiplier = 1000;
        numStr = cleaned.replace('K', '');
      } else if (cleaned.includes('%')) {
        numStr = cleaned.replace('%', '');
      }
      
      const num = parseFloat(numStr);
      return isNaN(num) ? 0 : num * multiplier;
    };

    const startValue = parseNumericValue(previousValueRef.current);
    const endValue = parseNumericValue(formattedValue);
    
    // If values are the same or animation disabled, update immediately
    // Also immediately update if transitioning to/from empty string (no animation for empty values)
    if (!shouldAnimate || startValue === endValue || formattedValue === 'NA' || previousValueRef.current === 'NA' ||
        formattedValue === '' || formattedValue === null || formattedValue === undefined ||
        (formattedValue === '' && previousValueRef.current !== '')) {
      setDisplayValue(formattedValue || '');
      previousValueRef.current = formattedValue || '';
      return;
    }
    
    // If transitioning to empty, don't animate - just set to empty immediately
    if (formattedValue === '' || formattedValue === null || formattedValue === undefined) {
      setDisplayValue('');
      previousValueRef.current = '';
      return;
    }

    // Clear any existing animation
    if (animationRef.current) {
      clearInterval(animationRef.current);
    }

    // Calculate steps needed based on difference
    const difference = endValue - startValue;
    const absDifference = Math.abs(difference);
    
    // Calculate steps: more steps for larger differences, but cap at reasonable number
    let steps;
    if (absDifference < 100) {
      steps = Math.max(10, Math.floor(absDifference / 5));
    } else if (absDifference < 1000) {
      steps = Math.max(20, Math.floor(absDifference / 20));
    } else if (absDifference < 10000) {
      steps = Math.max(30, Math.floor(absDifference / 50));
    } else {
      steps = Math.max(40, Math.min(80, Math.floor(absDifference / 200)));
    }
    
    const stepSize = difference / steps;
    let currentStep = 0;

    // Format value during animation - preserve original format
    const formatAnimatedValue = (num) => {
      const originalStr = String(originalValue);
      
      // If original has currency symbol, preserve it
      const hasDollar = originalStr.includes('$');
      const hasEuro = originalStr.includes('€');
      
      // If original has 'M' (not in parentheses), format as million
      if (originalStr.includes('M') && !originalStr.includes('(M)')) {
        const millions = num / 1000000;
        return (hasDollar ? '$' : hasEuro ? '€' : '') + millions.toFixed(2) + 'M';
      }
      // If original has 'B', format as billion
      if (originalStr.includes('B')) {
        const billions = num / 1000000000;
        return (hasDollar ? '$' : hasEuro ? '€' : '') + billions.toFixed(2) + 'B';
      }
      // If original has 'K', format as thousand
      if (originalStr.includes('K')) {
        const thousands = num / 1000;
        return (hasDollar ? '$' : hasEuro ? '€' : '') + thousands.toFixed(2) + 'K';
      }
      // If original has '%', format as percentage
      if (originalStr.includes('%')) {
        return num.toFixed(2) + '%';
      }
      // If original has '+' at the end
      if (originalStr.includes('+')) {
        return Math.round(num).toLocaleString() + '+';
      }
      // If original has ' ktCO₂' or ' ktCO₂ avoided'
      if (originalStr.includes('ktCO₂')) {
        return Math.round(num).toLocaleString() + (originalStr.includes('avoided') ? ' ktCO₂ avoided' : ' ktCO₂');
      }
      // If original has ' MW'
      if (originalStr.includes('MW')) {
        return Math.round(num).toLocaleString() + ' MW';
      }
      // If original has ' households'
      if (originalStr.includes('households')) {
        return Math.round(num).toLocaleString() + ' households';
      }
      // If original has ' connections'
      if (originalStr.includes('connections')) {
        // If num is 0 or less, return empty string (don't show "0 connections")
        if (num <= 0) {
          return '';
        }
        return Math.round(num).toLocaleString() + ' connections';
      }
      // If original has ' subscribers'
      if (originalStr.includes('subscribers')) {
        // If num is 0 or less, return empty string (don't show "0 subscribers")
        if (num <= 0) {
          return '';
        }
        return Math.round(num).toLocaleString() + ' subscribers';
      }
      // If original has ' jobs'
      if (originalStr.includes('jobs')) {
        return Math.round(num).toLocaleString() + ' jobs';
      }
      
      // Default: format with commas
      return Math.round(num).toLocaleString();
    };

    // Animate the number
    animationRef.current = setInterval(() => {
      currentStep++;
      const currentValue = startValue + (stepSize * currentStep);
      
      if (currentStep >= steps) {
        setDisplayValue(formattedValue);
        previousValueRef.current = formattedValue;
        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
      } else {
        const formatted = formatAnimatedValue(currentValue);
        // Don't show "0" or "0 connections" or "0 subscribers" during animation - show empty instead
        if (formatted === '0' || formatted === '0 connections' || formatted === '0 subscribers' || formatted.trim() === '') {
          setDisplayValue('');
        } else {
          setDisplayValue(formatted);
        }
      }
    }, animationSpeed);

    // Cleanup
    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [formattedValue, shouldAnimate, animationSpeed]);

  return displayValue;
};

const ImpactInsightsPage = () => {
  const [selectedRegion, setSelectedRegion] = useState("Northern Africa");
  const [selectedCountry, setSelectedCountry] = useState("ALL");
  const [impactData, setImpactData] = useState({});
  const [prevImpactData, setPrevImpactData] = useState({});
  const [metrics, setMetrics] = useState(null);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countsLoading, setCountsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countsError, setCountsError] = useState(null);
  const [regionList, setRegionList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const previousRegionRef = useRef(null);
  const previousCountryRef = useRef(null);
  const isFirstLoadRef = useRef(true);
  const [jobsData, setJobsData] = useState(null);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [selectedRegionJobsData, setSelectedRegionJobsData] = useState({});
  const [selectedRegionJobsLoading, setSelectedRegionJobsLoading] = useState(true);
  const [impactStories, setImpactStories] = useState([]);
  const [storiesLoading, setStoriesLoading] = useState(true);
  const [beneficiaryData, setBeneficiaryData] = useState(null);
  const [beneficiaryLoading, setBeneficiaryLoading] = useState(true);

  // Scroll to top immediately when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);


  // Icon names mapping to image files
  const ICONS = {
    STREET_LIGHTS: 'street-lights.png',
    SCHOOL: 'school.png',
    SMART_GRID: 'smart-grid.png',
    ELECTRIC_POWER: 'electric-power.png',
    CLIMATE_IMPACT: 'climate-impact.png',
    POPULATION: 'population.png',
    HEALTHCARE: 'healthcare.png',
    ECONOMIC_IMPACT: 'economic-impact.png',
    SOCIAL_IMPACT: 'social-impact.png',
    ENERGY_ACCESS: 'energy-access.png',
    POWER_GRID: 'power-grid.png',
    ECONOMICS: 'economics.png',
    CLEAN_COOKING: 'clean-cooking.png'
  };

  // Icon component without color filter (using colored icons)
  const Icon = ({ iconName, className = '', alt = '' }) => (
    <img
      src={`/images/impact-insights/${iconName}`}
      alt={alt || iconName}
      className={`icon-image ${className}`}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain'
      }}
    />
  );

  // Helper function to get icon for story based on title/keywords
  const getStoryIcon = (title, story) => {
    const text = ((title || '') + ' ' + (story || '')).toLowerCase();
    if (text.includes('school') || text.includes('education') || text.includes('student')) {
      return ICONS.SCHOOL;
    }
    if (text.includes('women') || text.includes('entrepreneur') || text.includes('business')) {
      return ICONS.SOCIAL_IMPACT;
    }
    if (text.includes('water') || text.includes('pump') || text.includes('access')) {
      return ICONS.ENERGY_ACCESS;
    }
    if (text.includes('health') || text.includes('clinic') || text.includes('hospital')) {
      return ICONS.HEALTHCARE;
    }
    return ICONS.ENERGY_ACCESS; // default icon
  };

  // Fetch metrics from API
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${API_BASE}/impact-insight/project-insight-total-values`);

        if (!response.ok) {
          throw new Error('Failed to fetch metrics');
        }

        const data = await response.json();
        // Backend returns { success: true, data: {...} }
        setMetrics(data.data || data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  // Trigger animation for key metrics on initial page load when metrics data is first available
  const metricsFirstLoadRef = useRef(true);
  useEffect(() => {
    if (metricsFirstLoadRef.current && !loading && metrics) {
      metricsFirstLoadRef.current = false;
      // Small delay to ensure values are set, then trigger animation for key metrics on first load
      setTimeout(() => {
        setShouldAnimate(true);
        setTimeout(() => setShouldAnimate(false), 2000);
      }, 100);
    }
  }, [loading, metrics]);


  // Fetch counts from API
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        setCountsLoading(true);
        setCountsError(null);
        const url = `${API_BASE}/impact-insight/total-projects-counts`;

        const response = await fetch(url);

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch counts: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (data.success && data.totalProjects !== undefined) {
          setCounts(data);
        } else {
          throw new Error(data.message || 'Invalid response format');
        }
      } catch (err) {
        setCountsError(err.message);
        // Set default counts on error so UI doesn't break
        setCounts({ totalProjects: 0 });
      } finally {
        setCountsLoading(false);
      }
    };

    fetchCounts();
  }, []);

  // Format numbers for display
  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0';
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(1) + 'B';
    } else if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return Math.round(num).toLocaleString();
  };

  // Format currency (API returns USD)
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '$0';
    if (amount >= 1000000000) {
      return '$' + (amount / 1000000000).toFixed(1) + 'B';
    } else if (amount >= 1000000) {
      return '$' + (amount / 1000000).toFixed(1) + 'M';
    } else if (amount >= 1000) {
      return '$' + (amount / 1000).toFixed(1) + 'K';
    }
    return '$' + Math.round(amount).toLocaleString();
  };

  // Format story metric value with comma formatting for numbers
  const formatStoryMetric = (value) => {
    if (!value || value === '—' || value === '-') return value;
    
    // If it's a number, format with commas
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    
    // If it's a string, extract and format all numeric values
    if (typeof value === 'string') {
      // Match all sequences of digits (numbers) in the string
      // This will format numbers like 95000 to 95,000 while preserving text
      return value.replace(/\b(\d+)\b/g, (match) => {
        const number = parseInt(match, 10);
        if (!isNaN(number)) {
          // Format with commas
          return number.toLocaleString();
        }
        return match;
      });
    }
    
    return value;
  };

  // Format numeric values in impact_story text with commas
  const formatImpactStory = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Match all sequences of digits (numbers) in the string and format with commas
    // This will format numbers like 218400 to 218,400 and 778000 to 778,000
    return text.replace(/\b(\d+)\b/g, (match) => {
      const number = parseInt(match, 10);
      if (!isNaN(number)) {
        // Format with commas
        return number.toLocaleString();
      }
      return match;
    });
  };

  // Helper function to format beneficiary value (returns "NA" for 0 or null/undefined)
  const formatBeneficiaryValue = (value) => {
    if (value === null || value === undefined || value === 0) {
      return "NA";
    }
    return formatNumber(value);
  };

  // Beneficiary data - from API (memoized to prevent unnecessary recalculations)
  const beneficiaryDataMemo = useMemo(() => {
    // Use API data if available, otherwise return default values
    if (beneficiaryData && typeof beneficiaryData === 'object' && Object.keys(beneficiaryData).length > 0) {
      return {
        healthcare: { 
          value: beneficiaryData.healthcare_value ?? 0, 
          label: 'Healthcare Facilities', 
          icon: '🏥', 
          hasData: true 
        },
        streetLights: { 
          value: beneficiaryData.street_lights_value ?? 0, 
          label: 'Street Lights', 
          icon: '💡', 
          hasData: true 
        },
        businessImpacted: {
          value: beneficiaryData.business_impacted ?? 0,
          label: 'Business Impacted',
          icon: '👩‍💼',
          hasData: true
        },
        subscribers: { 
          value: beneficiaryData.subscribers_value ?? 0, 
          label: 'Subscribers', 
          icon: '👥', 
          hasData: true 
        },
        publicInstitute: {
          value: beneficiaryData.public_institute_value ?? 0,
          label: 'Public Institute',
          icon: '🏛️',
          hasData: true
        },
        transmissionLines: {
          value: beneficiaryData.transmission_lines_value ?? 0,
          label: 'Transmission Lines',
          icon: '⚡',
          hasData: true
        }
      };
    }

    // Fallback to default values if no API data
    return {
      healthcare: { value: 0, label: 'Healthcare Facilities', icon: '🏥', hasData: false },
      streetLights: { value: 0, label: 'Street Lights', icon: '💡', hasData: false },
      businessImpacted: { value: 0, label: 'Business Impacted', icon: '👩‍💼', hasData: false },
      subscribers: { value: 0, label: 'Subscribers', icon: '👥', hasData: false },
      publicInstitute: { value: 0, label: 'Public Institute', icon: '🏛️', hasData: false },
      transmissionLines: { value: 0, label: 'Transmission Lines', icon: '⚡', hasData: false }
    };
  }, [beneficiaryData]);

  // Small Pie Chart Configuration Helper
  const createSmallPieChart = (value, color, title) => ({
    tooltip: {
      trigger: 'item',
      formatter: `${title}: {d}%`
    },
    legend: {
      show: false
    },
    grid: {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0
    },
    series: [
      {
        name: title,
        type: 'pie',
        radius: ['45%', '70%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 0,
          borderColor: '#fff',
          borderWidth: 1.5
        },
        label: {
          show: true,
          position: 'center',
          formatter: '{d}%',
          fontSize: 12,
          fontWeight: 700,
          color: color
        },
        labelLine: {
          show: false
        },
        data: [
          { value: value, name: title, itemStyle: { color: color } },
          { value: 100 - value, name: 'Other', itemStyle: { color: '#f0f0f0' } }
        ],
        animation: true,
        animationDuration: 800,
        animationEasing: 'cubicOut'
      }
    ]
  });

  // 🟢 Fetch jobs data for all regions (for the new card layout) - Single API call
  // Helper function to map database region names to display names
  const getRegionDisplayName = (dbRegionName) => {
    const regionMapping = {
      'Northern Africa': 'North Africa',
      'Western Africa': 'West Africa',
      'Eastern Africa': 'East Africa',
      'Central Africa': 'Central Africa',
      'Southern Africa': 'Southern Africa'
    };
    return regionMapping[dbRegionName] || dbRegionName;
  };

  // Fetch jobs data when selectedRegion changes
  useEffect(() => {
    if (!selectedRegion) {
      setSelectedRegionJobsData({});
      setSelectedRegionJobsLoading(false);
      return;
    }

    const fetchSelectedRegionJobsData = async () => {
      setSelectedRegionJobsLoading(true);
      try {
        const response = await fetch(
          `${API_BASE}/impact-insight/jobs-economic-growth`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              region: selectedRegion,
              country: selectedCountry || "ALL",
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          console.log("Selected region jobs data received:", data);
          setSelectedRegionJobsData(data.result || {});
        } else {
          const errorText = await response.text();
          console.error("Error fetching selected region jobs data:", errorText);
          setSelectedRegionJobsData({});
        }
      } catch (err) {
        console.error("Error fetching selected region jobs data:", err);
        setSelectedRegionJobsData({});
      } finally {
        setSelectedRegionJobsLoading(false);
      }
    };

    fetchSelectedRegionJobsData();
  }, [selectedRegion, selectedCountry]);

  // 🟢 Fetch jobs data when region/country changes
  useEffect(() => {
    if (!selectedRegion) return;

    const fetchJobsData = async () => {
      setJobsLoading(true);
      try {
        const response = await fetch(
          `${API_BASE}/impact-insight/jobs-economic-growth`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              region: selectedRegion,
              country: selectedCountry || "ALL",
            }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Jobs API Error Response:", errorText);
          throw new Error("Failed to fetch jobs data");
        }

        const data = await response.json();
        // Backend returns { success: true, filters: {...}, result: {...} }
        setJobsData(data.result || {});
      } catch (err) {
        console.error("Error fetching jobs data:", err);
        setJobsData(null);
      } finally {
        setJobsLoading(false);
      }
    };

    fetchJobsData();
  }, [selectedRegion, selectedCountry]);

  // 🟢 Fetch top impact stories when region/country changes
  useEffect(() => {
    if (!selectedRegion) return;

    const fetchImpactStories = async () => {
      setStoriesLoading(true);
      try {
        const response = await fetch(
          `${API_BASE}/impact-insight/top-impact-stories`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              region: selectedRegion,
              country: selectedCountry || "ALL",
            }),
          }
        );

        if (!response.ok) throw new Error("Failed to fetch impact stories");

        const data = await response.json();
        setImpactStories(data.topStories || []);
      } catch (err) {
        setImpactStories([]);
      } finally {
        setStoriesLoading(false);
      }
    };

    fetchImpactStories();
  }, [selectedRegion, selectedCountry]);

  // 🟢 Fetch beneficiary insights when region/country changes
  useEffect(() => {
    const fetchBeneficiaryData = async () => {
      setBeneficiaryLoading(true);
      try {
        const response = await fetch(
          `${API_BASE}/impact-insight/beneficiary-insights`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (!response.ok) throw new Error("Failed to fetch beneficiary data");

        const data = await response.json();
        
        // Handle both response structures: { success, data: {...} } or direct {...}
        const beneficiaryData = data.data || data;
        
        // Ensure we have a valid object (not null or undefined)
        if (beneficiaryData && typeof beneficiaryData === 'object' && !Array.isArray(beneficiaryData)) {
          setBeneficiaryData(beneficiaryData);
        } else {
          setBeneficiaryData({});
        }
      } catch (err) {
        setBeneficiaryData(null);
      } finally {
        setBeneficiaryLoading(false);
      }
    };

    fetchBeneficiaryData();
  }, []);

  // Trigger animation when beneficiary data loads
  useEffect(() => {
    if (!beneficiaryLoading && beneficiaryData) {
      // Trigger animation on initial load
      setShouldAnimate(true);
      setTimeout(() => setShouldAnimate(false), 2000);
    }
  }, [beneficiaryLoading, beneficiaryData]);

  // Check if there's any meaningful jobs data to display
  // Only hide section after loading is complete and there's no data
  const hasJobsData = () => {
    // Show section while loading
    if (jobsLoading) return true;
    
    // Hide if no data object
    if (!jobsData) return false;
    
    // Check if any key field has a meaningful value (greater than 0 or not null/undefined)
    const hasValue = (value) => {
      return value !== null && value !== undefined && value !== 0 && value !== '0' && value !== '';
    };
    
    return (
      hasValue(jobsData.total_jobs_created) ||
      hasValue(jobsData.permanent_value) ||
      hasValue(jobsData.temporary_value) ||
      hasValue(jobsData.direct_value) ||
      hasValue(jobsData.indirect_value) ||
      hasValue(jobsData.construction_value) ||
      hasValue(jobsData.operation_value) ||
      hasValue(jobsData.technicians_value) ||
      hasValue(jobsData.women_in_percent) ||
      hasValue(jobsData.youth_value) ||
      hasValue(jobsData.women_smes_value)
    );
  };

  const hasBeneficiaryData = () => {
    // Show section while loading
    if (beneficiaryLoading) return true;
    
    // Hide if no data object
    if (!beneficiaryData) return false;
    
    // Check if any key field has a meaningful value (greater than 0 or not null/undefined)
    const hasValue = (value) => {
      return value !== null && value !== undefined && value !== 0 && value !== '0' && value !== '';
    };
    
    return (
      hasValue(beneficiaryData.healthcare_value) ||
      hasValue(beneficiaryData.rural_school_value) ||
      hasValue(beneficiaryData.street_lights_value) ||
      hasValue(beneficiaryData.subscribers_value) ||
      hasValue(beneficiaryData.business_headed_by_women_value) ||
      hasValue(beneficiaryData.clean_cooking_value)
    );
  };

  // Helper to check if value is valid (not null, undefined, 0, or empty)
  const isValidJobValue = (value) => {
    return value !== null && value !== undefined && value !== 0 && value !== '0' && value !== '';
  };

  // Jobs Table Data - Construction, Operational, Technicians (all in %)
  // Calculate percentages based on total jobs
  const totalJobsForPercentage = jobsData?.total_jobs_created || 
    (jobsData?.construction_value || 0) + 
    (jobsData?.operation_value || 0) + 
    (jobsData?.technicians_value || 0);
  
  const calculatePercentage = (value) => {
    if (!totalJobsForPercentage || totalJobsForPercentage === 0) return '0%';
    return `${Math.round((value / totalJobsForPercentage) * 100)}%`;
  };

  const jobsTableData = [
    { 
      icon: ICONS.POWER_GRID, 
      label: 'Construction', 
      value: calculatePercentage(jobsData?.construction_value || 0) 
    },
    { 
      icon: ICONS.SMART_GRID, 
      label: 'Operational', 
      value: calculatePercentage(jobsData?.operation_value || 0) 
    },
    { 
      icon: ICONS.SMART_GRID, 
      label: 'Technicians', 
      value: calculatePercentage(jobsData?.technicians_value || 0) 
    }
  ];

  // 🟢 Fetch impact data when region/country changes - REMOVED DUPLICATE (see line 1035)

  // Track changes to trigger animation
  useEffect(() => {
    // Trigger animation on first page load
    if (isFirstLoadRef.current) {
      isFirstLoadRef.current = false;
      // Wait a bit for data to load, then trigger animation
      setTimeout(() => {
        setShouldAnimate(true);
        setTimeout(() => setShouldAnimate(false), 2000);
      }, 500);
    }
    
    // Trigger animation on region change
    if (previousRegionRef.current !== null && previousRegionRef.current !== selectedRegion) {
      setShouldAnimate(true);
      setTimeout(() => setShouldAnimate(false), 2000);
    }
    previousRegionRef.current = selectedRegion;
  }, [selectedRegion]);

  useEffect(() => {
    // Trigger animation on country change
    if (previousCountryRef.current !== null && previousCountryRef.current !== selectedCountry) {
      setShouldAnimate(true);
      setTimeout(() => setShouldAnimate(false), 2000);
    }
    previousCountryRef.current = selectedCountry;
  }, [selectedCountry]);

  // Animation speed in milliseconds (lower = faster)
  const animationSpeed = 15;

  // Country Region Wise Impact Insights
  //  useEffect(() => {
  //   const fetchImpactData = async () => {
  //     if (!selectedRegion) return; // avoid sending invalid request
  //     setLoading(true);
  //     setError("");
  //     try {
  //       const response = await fetch(
  //         `${API_BASE}/impact-insight/project-insights-country-region-wise`,
  //         {
  //           method: "POST",
  //           headers: { "Content-Type": "application/json" },
  //           body: JSON.stringify({
  //             region: selectedRegion,
  //             country: selectedCountry,
  //           }),
  //         }
  //       );

  //       if (!response.ok) throw new Error("Failed to fetch data");
  //       const data = await response.json();
  //       setImpactData(data.result || {});
  //     } catch (err) {
  //       console.error(err);
  //       setError("Unable to load impact data");
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchImpactData();
  // }, [selectedRegion, selectedCountry]);

   // 🟢 Load region list on page load
   useEffect(() => {
    const fetchRegionsOnLoad = async () => {
      try {
        const res = await fetch(`${API_BASE}/impact-insight/region-country-list`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}), // no region — returns all regions
        });
        const data = await res.json();
        if (data.success && data.regions) {
          setRegionList(data.regions);
        }
      } catch (err) {
      }
    };
    fetchRegionsOnLoad();
  }, []);

  // 🟢 Fetch country list whenever region changes
  useEffect(() => {
    if (!selectedRegion) {
      setCountryList([]);
      return;
    }

    const fetchCountries = async () => {
      try {
        const res = await fetch(`${API_BASE}/impact-insight/region-country-list`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ region: selectedRegion }),
        });
        const data = await res.json();
        if (data.success && data.countries) {
          setCountryList(["ALL", ...data.countries]);
          setSelectedCountry("ALL"); // reset when region changes
        }
      } catch (err) {
      }
    };

    fetchCountries();
  }, [selectedRegion]);

  // 🟢 Fetch impact data whenever region or country changes
  useEffect(() => {
    if (!selectedRegion) return;

    const fetchImpactData = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(
          `${API_BASE}/impact-insight/project-insights-country-region-wise`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              region: selectedRegion,
              country: selectedCountry || "ALL",
            }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error("API Error Response:", errorText);
          throw new Error("Failed to fetch data");
        }

        const data = await response.json();
        
        // ✅ Store previous data for smooth transitions before updating
        setPrevImpactData(impactData);
        setImpactData(data.result || {});
      } catch (err) {
        console.error("Error fetching impact data:", err);
        setError("Unable to load impact data");
        setImpactData({}); // Set empty object on error
      } finally {
        setLoading(false);
      }
    };

    fetchImpactData();
  }, [selectedRegion, selectedCountry]);





  // ✅ Extract data safely
  const summary = impactData?.summary || {};
  const details = impactData?.details || {};

  // Calculate totalJobs and other job metrics from API data
  const totalJobs = jobsData?.total_jobs_created;
  const newConnections = details.energy_access?.connections || 12091;
  // Use API data for metrics
  const womenPercentage = jobsData?.women_in_percent 
    ? `${Math.round(jobsData.women_in_percent)}%` 
    : 'NA';
  const youthTargetedPercentage = jobsData?.youth_value 
    ? `${Math.round(jobsData.youth_value)}%` 
    : 'NA';
  const businessHeadedByWomen = jobsData?.women_smes_value 
    ? formatNumber(jobsData.women_smes_value)
    : 'NA';

  // Use animated numbers for all values
  const animatedCleanEnergyProjects = useAnimatedNumber(
    metrics ? formatNumber(metrics["Total Beneficiaries Reached"] || 0) : '0',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedInstalledCapacity = useAnimatedNumber(
    metrics ? formatNumber(metrics["Installed Renewable Capacity (MW)"] || 0) + ' MW' : '0 MW',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedTotalInvestment = useAnimatedNumber(
    metrics ? formatCurrency(metrics["Total Investment (USD)"] || 0) : '$0',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedCO2Reduced = useAnimatedNumber(
    metrics ? formatNumber(metrics["CO2 Emissions Reduced (tons)"] || 0) + ' ktCO₂' : '0 ktCO₂',
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedJobsCreated = useAnimatedNumber(
    metrics ? formatNumber(metrics["Jobs Created"] || 0) + '+' : '0+',
    shouldAnimate && !loading,
    animationSpeed
  );
  
  // Detailed impact cards animated values
  const animatedHouseholds = useAnimatedNumber(
    impactData.details?.energy_access?.households ?? prevImpactData.details?.energy_access?.households
      ? `${(impactData.details?.energy_access?.households || prevImpactData.details?.energy_access?.households).toLocaleString()} households`
      : "NA",
    shouldAnimate && !loading,
    animationSpeed
  );
  // Only create animated connections if value exists and is > 0
  const connectionsValue = details.energy_access?.connections && details.energy_access.connections > 0
    ? details.energy_access.connections
    : null;
  
  const animatedConnections = useAnimatedNumber(
    connectionsValue ? `${connectionsValue.toLocaleString()} connections` : "",
    shouldAnimate && !loading && connectionsValue !== null,
    animationSpeed
  );
  // Animate only the number, label is static
  const emissionsValue = summary.emissions_avoided_tCO2 || prevImpactData.summary?.emissions_avoided_tCO2;
  const animatedEmissionsAvoidedDetail = useAnimatedNumber(
    emissionsValue ? emissionsValue.toLocaleString() : "NA",
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedJobsDetail = useAnimatedNumber(
    summary.jobs_created ? `${summary.jobs_created.toLocaleString()} jobs` : "NA",
    shouldAnimate && !loading,
    animationSpeed
  );
  // Transmission Lines animated value (from impact API)
  const transmissionLinesValue = details.transmission_lines || prevImpactData.details?.transmission_lines;
  const animatedTransmissionLinesImpact = useAnimatedNumber(
    transmissionLinesValue ? `${transmissionLinesValue.toLocaleString()} km` : "NA",
    shouldAnimate && !loading,
    animationSpeed
  );
  // Animate only the number, label is static
  const investmentValue = summary.total_commitments_usd || prevImpactData.summary?.total_commitments_usd;
  const animatedInvestmentDetail = useAnimatedNumber(
    investmentValue ? formatCurrency(investmentValue).replace('$', '') : "NA",
    shouldAnimate && !loading,
    animationSpeed
  );
  // Extract numeric value from API, animate only the number (label is static)
  const innovationPeopleValue = details.energy_access?.innovation || prevImpactData.details?.energy_access?.innovation;
  const animatedInnovation = useAnimatedNumber(
    innovationPeopleValue 
      ? formatNumber(innovationPeopleValue)
      : "NA",
    shouldAnimate && !loading,
    animationSpeed
  );
  
  // Only create animated subscribers if value exists and is > 0 (similar to connections)
  const subscribersValue = (impactData.details?.energy_access?.subscribers ?? prevImpactData.details?.energy_access?.subscribers) && 
    (impactData.details?.energy_access?.subscribers ?? prevImpactData.details?.energy_access?.subscribers) > 0
    ? (impactData.details?.energy_access?.subscribers ?? prevImpactData.details?.energy_access?.subscribers)
    : null;
  
  const animatedSubscribersDetail = useAnimatedNumber(
    subscribersValue ? `${subscribersValue.toLocaleString()} subscribers` : "",
    shouldAnimate && !loading && subscribersValue !== null,
    animationSpeed
  );
  
  // Beneficiary cards animated values (using memoized data, showing "NA" for zero values)
  const animatedHealthcare = useAnimatedNumber(
    formatBeneficiaryValue(beneficiaryDataMemo.healthcare.value),
    shouldAnimate && !beneficiaryLoading,
    animationSpeed
  );
  const animatedStreetLights = useAnimatedNumber(
    formatBeneficiaryValue(beneficiaryDataMemo.streetLights.value),
    shouldAnimate && !beneficiaryLoading,
    animationSpeed
  );
  const animatedBusinessImpacted = useAnimatedNumber(
    formatBeneficiaryValue(beneficiaryDataMemo.businessImpacted.value),
    shouldAnimate && !beneficiaryLoading,
    animationSpeed
  );
  const animatedSubscribers = useAnimatedNumber(
    beneficiaryDataMemo.subscribers.value === 0 || beneficiaryDataMemo.subscribers.value === null || beneficiaryDataMemo.subscribers.value === undefined
      ? "NA"
      : formatNumber(beneficiaryDataMemo.subscribers.value) + '+',
    shouldAnimate && !beneficiaryLoading,
    animationSpeed
  );
  const animatedPublicInstitute = useAnimatedNumber(
    formatBeneficiaryValue(beneficiaryDataMemo.publicInstitute.value),
    shouldAnimate && !beneficiaryLoading,
    animationSpeed
  );
  const animatedTransmissionLines = useAnimatedNumber(
    formatBeneficiaryValue(beneficiaryDataMemo.transmissionLines.value),
    shouldAnimate && !beneficiaryLoading,
    animationSpeed
  );
  
  // Jobs section animated values
  const animatedNewConnections = useAnimatedNumber(
    newConnections.toLocaleString(),
    shouldAnimate && !loading,
    animationSpeed
  );
  const animatedTotalJobs = useAnimatedNumber(
    totalJobs ? totalJobs.toLocaleString() : "NA",
    shouldAnimate && !jobsLoading,
    animationSpeed
  );

  return (
    <>
      <Helmet>
        <title>Africa Energy Trends | Impact Insights</title>
        <meta property="og:title" content="Africa Energy Trends | Impact Insights" />
        <meta property="og:description" content="Access deep insights into Africa's evolving energy landscape. Discover insights & trends that are shaping the energy transition & sustainable development." />
        <meta property="og:image" content={`${SITE_BASE_URL}/og-images/Impact-Insight.png`} />
        <meta property="og:image:secure_url" content={`${SITE_BASE_URL}/og-images/Impact-Insight.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={`${SITE_BASE_URL}/impact-insights`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Africa Energy Access Trends" />
        <meta name="description" content="Access deep insights into Africa's evolving energy landscape. Discover insights & trends that are shaping the energy transition & sustainable development." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Africa Energy Trends | Impact Insights" />
        <meta name="twitter:description" content="Access deep insights into Africa's evolving energy landscape. Discover insights & trends that are shaping the energy transition & sustainable development." />
        <meta name="twitter:image" content={`${SITE_BASE_URL}/og-images/Impact-Insight.png`} />
      </Helmet>
      <div className="impact-insights-page">
        {/* Impact Insights Main Overview */}
      <section className="impact-overview-section">
        <div className="container">
          <div className="section-header">
            <h1 className="section-title">Impact Insights</h1>
            <p className="section-subtitle">
              {countsError ? (
                'Covering multiple countries, regions, and projects'
              ) : counts && counts.totalProjects !== undefined ? (
                `Covering 55 countries | 5 regions | ${counts.totalProjects}+ projects`
              ) : (
                'Covering multiple countries, regions, and projects'
              )}
            </p>
          </div>

          {error ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#dc3545' }}>
              <p>Error loading metrics: {error}</p>
            </div>
          ) : (
            <div className="key-metrics-grid">
              <div className="metric-card">
                <div className="metric-value">
                  {animatedCleanEnergyProjects}
                </div>
                <div className="metric-label">Total Beneficiaries Reached</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">
                  {animatedInstalledCapacity}
                </div>
                <div className="metric-label">Installed Renewable Capacity</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">
                  {animatedTotalInvestment}
                </div>
                <div className="metric-label">Total Investment</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">
                  {animatedCO2Reduced}
                </div>
                <div className="metric-label">CO₂ Emissions Reduced</div>
              </div>
              <div className="metric-card">
                <div className="metric-value">
                  {animatedJobsCreated}
                </div>
                <div className="metric-label">Jobs Created</div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Beneficiary Insights - Redesigned */}
      <section className="beneficiary-insights-section">
        <div className="container">
          <div className="section-header">
            <h2 className="sub-section-title">Beneficiary Insights for Africa</h2>
            <p className="section-subtitle">Understanding how energy investments are changing lives across country</p>
          </div>

          <div className="beneficiary-split-layout">
            {/* Left Side - Image Slider */}
            <div className="beneficiary-image-container">
            <Slider
              dots={true}
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              autoplay={true}
              autoplaySpeed={3000}
              arrows={false}
              fade={true}
              cssEase="linear"
            >
              <div className="beneficiary-slide">
                <img
                  src="/images/benefits-slider-img/healthcare.jpg"
                  alt="Healthcare facility with access to reliable electricity"
                  className="beneficiary-main-image"
                />
              </div>
              <div className="beneficiary-slide">
                <img
                  src="/images/benefits-slider-img/clean-cooking.jpg"
                  alt="Clean cooking energy solution in African household"
                  className="beneficiary-main-image"
                />
              </div>
              <div className="beneficiary-slide">
                <img
                  src="/images/benefits-slider-img/solar-street-lighting.jpg"
                  alt="Solar street lighting installation in African community"
                  className="beneficiary-main-image"
                />
              </div>
            </Slider>
          </div>

          {/* Right Side - Cards Grid */}
          <div className="beneficiary-cards-grid">
            <div className="beneficiary-card">
              <div className="beneficiary-card-icon"><Icon iconName={ICONS.HEALTHCARE} alt="Healthcare Access" /></div>
              <div className="beneficiary-card-number">{animatedHealthcare}</div>
              <h3 className="beneficiary-card-title">Healthcare Access</h3>
              <p className="beneficiary-card-description">
                Healthcare facilities powered by clean energy
              </p>
            </div>

            <div className="beneficiary-card">
              <div className="beneficiary-card-icon"><Icon iconName={ICONS.STREET_LIGHTS} alt="Street Lighting" /></div>
              <div className="beneficiary-card-number">{animatedStreetLights}</div>
              <h3 className="beneficiary-card-title">Street Lighting</h3>
              <p className="beneficiary-card-description">
                Street lights illuminating communities
              </p>
            </div>

            <div className="beneficiary-card">
              <div className="beneficiary-card-icon"><Icon iconName={ICONS.SOCIAL_IMPACT} alt="Business Impacted" /></div>
              <div className="beneficiary-card-number">{animatedBusinessImpacted}</div>
              <h3 className="beneficiary-card-title">Business Impacted</h3>
              <p className="beneficiary-card-description">
                Businesses impacted by clean energy initiatives
              </p>
            </div>

            <div className="beneficiary-card">
              <div className="beneficiary-card-icon"><Icon iconName={ICONS.POPULATION} alt="Subscribers" /></div>
              <div className="beneficiary-card-number">{animatedSubscribers}</div>
              <h3 className="beneficiary-card-title">Subscribers</h3>
              <p className="beneficiary-card-description">
                Active subscribers benefiting from clean energy
              </p>
            </div>

            <div className="beneficiary-card">
              <div className="beneficiary-card-icon"><Icon iconName={ICONS.SCHOOL} alt="Public Institute" /></div>
              <div className="beneficiary-card-number">{animatedPublicInstitute}</div>
              <h3 className="beneficiary-card-title">Public Institute</h3>
              <p className="beneficiary-card-description">
                Public institutions with access to clean energy
              </p>
            </div>

            <div className="beneficiary-card">
              <div className="beneficiary-card-icon"><Icon iconName={ICONS.POWER_GRID} alt="Transmission Lines" /></div>
              <div className="beneficiary-card-number">{animatedTransmissionLines}</div>
              <h3 className="beneficiary-card-title">Transmission Lines</h3>
              <p className="beneficiary-card-description">
                Transmission lines installed for energy distribution (km)
              </p>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* UN Sustainable Development Goals Alignment */}
      <section className="sdg-alignment-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">UN Sustainable Development Goals Alignment</h2>
            <p className="section-description">Our clean energy projects directly contribute to achieving the UN's 2030 Agenda for Sustainable Development.</p>
          </div>

          <div className="sdg-highlight-box">
            <div className="sdg-content">
              <div className="sdg-percentage">92%</div>
              <div className="sdg-text">
                <strong>of tracked projects directly support SDG 7</strong>
                <p>Contributing to a sustainable and equitable energy future for Africa</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regional & Country Impact Dimensions */}
      <section className="regional-impact-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Regional Impact Dimensions</h2>
            <p className="section-description">
              Our comprehensive impact framework measures progress across energy,
              climate, social, economic, and innovation metrics.
            </p>
          </div>

          {/* 🔽 Filters */}
          <div className="filters-container">
            <div className="filter-group">
              <label>Region:</label>
              <select
                value={selectedRegion}
                onChange={(e) => {
                  setSelectedRegion(e.target.value);
                  setSelectedCountry("ALL"); // reset country when region changes
                }}
              >
                <option value="">Select Region</option>
                {regionList.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>

            </div>

            {/* <div className="filter-group">
              <label>Country:</label>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                disabled={!selectedRegion} // disable until region selected
              >
                {countryList.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>

            </div> */}
          </div>

          {/* 🌀 Loading / Error States */}
          {error && <p className="error-text">{error}</p>}

         {/* Always render the cards, even during loading */}
{(impactData || prevImpactData) && (
  <>
              {/* 🧩 Detailed Impact Cards */}
              <div className="detailed-impact-grid">
                {/* Energy Access */}
                <div className="impact-card">
                  <div className="impact-icon">
                    <Icon iconName={ICONS.ELECTRIC_POWER} alt="Energy Access" />
                  </div>
                  <h3 className="impact-title">Energy Access</h3>
                  {/* <div className="impact-value">
                    {details.energy_access?.households
                      ? `${details.energy_access.households.toLocaleString()} households`
                      : "NA"}
                  </div> */}
                  <div className="impact-value">
                    {animatedHouseholds}
                  </div>
                  {connectionsValue !== null && 
                   connectionsValue > 0 && 
                   animatedConnections && 
                   animatedConnections.trim() !== '' && 
                   animatedConnections !== '0' &&
                   animatedConnections !== '0 connections' &&
                   animatedConnections.includes('connections') && (
                    <p className="impact-connection-value">
                      <span>{animatedConnections}</span> 
                    </p>
                  )}
                  <p className="impact-description">
                    Households and Connections impacted
                  </p>
                </div>

                {/* Climate Impact */}
                <div className="impact-card">
                  <div className="impact-icon">
                    <Icon iconName={ICONS.CLIMATE_IMPACT} alt="Climate Impact" />
                  </div>
                  <h3 className="impact-title">Climate Impact</h3>
                  <div className="impact-value">
                    {animatedEmissionsAvoidedDetail !== "NA" ? (
                      <>
                        <span>{animatedEmissionsAvoidedDetail}</span> <span className="impact-static-label">ktCO₂ reduced</span>
                      </>
                    ) : (
                      "NA"
                    )}
                  </div>
                  <p className="impact-description">
                    Total CO₂ emissions avoided through clean energy solutions
                  </p>
                </div>

                {/* Transmission Lines */}
                <div className="impact-card">
                  <div className="impact-icon">
                    <Icon iconName={ICONS.POWER_GRID} alt="Transmission Lines" />
                  </div>
                  <h3 className="impact-title">Transmission Lines</h3>
                  <div className="impact-value">
                    {animatedTransmissionLinesImpact}
                  </div>
                  <p className="impact-description">
                    Transmission lines and substations installed
                  </p>
                </div>

                {/* Economic Impact */}
                <div className="impact-card">
                  <div className="impact-icon">
                    <Icon iconName={ICONS.ECONOMIC_IMPACT} alt="Economic Impact" />
                  </div>
                  <h3 className="impact-title">Economic Impact</h3>
                  <div className="impact-value">
                    {animatedInvestmentDetail !== "NA" ? (
                      <>
                        <span>${animatedInvestmentDetail}</span> <span className="impact-static-label">invested</span>
                      </>
                    ) : (
                      "NA"
                    )}
                  </div>
                  <p className="impact-description">
                    Total project investments and financial commitments
                  </p>
                </div>

                {/* Innovation / Population */}
                <div className="impact-card">
                  <div className="impact-icon">
                    <Icon iconName={ICONS.POPULATION} alt="Innovation" />
                  </div>
                  <h3 className="impact-title">Beneficiaries</h3>
                  <div className="impact-value">
                    {animatedInnovation !== "NA" ? (
                      <>
                        <span>{animatedInnovation}</span> <span className="impact-static-label">people</span>
                      </>
                    ) : (
                      "NA"
                    )}
                  </div>
                  {subscribersValue !== null && 
                   subscribersValue > 0 && 
                   animatedSubscribersDetail && 
                   animatedSubscribersDetail.trim() !== '' && 
                   animatedSubscribersDetail !== '0' &&
                   animatedSubscribersDetail !== '0 subscribers' &&
                   animatedSubscribersDetail.includes('subscribers') && (
                    <p className="impact-connection-value">
                      <span>{animatedSubscribersDetail}</span> 
                    </p>
                  )}
                  <p className="impact-description">
                    People directly benefiting from supported initiatives
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </section>



      {/* Jobs & Economic Growth - View By Region */}
      <section className="jobs-economic-section">
        <div className="container">
          <div className="section-header">
            <h2 className="sub-section-title">Jobs & Economic Growth</h2>
            <p className="section-subtitle">Regional breakdown of jobs by type, permanence, and gender impact.</p>
          </div>

          {selectedRegionJobsLoading ? (
            <div className="jobs-regions-loading">
              <p>Loading jobs data...</p>
            </div>
          ) : selectedRegion ? (
            <div className="jobs-regions-grid">
              {/* Selected Region Card */}
              {(() => {
                const regionData = selectedRegionJobsData || {};
                const totalJobs = regionData.total_jobs_created;
                const hasTotalJobs = isValidJobValue(totalJobs);
                const theme = 'orange';
                const displayRegionName = getRegionDisplayName(selectedRegion);

                return (
                  <div key={selectedRegion} className={`jobs-region-card jobs-region-card-${theme}`}>
                    <div className="jobs-region-header">
                      <h3 className="jobs-region-name">{displayRegionName}</h3>
                      <div className="jobs-region-total">
                        {hasTotalJobs ? (
                          <>
                            <span className="jobs-region-total-value">{formatNumber(totalJobs)}</span>
                            <span className="jobs-region-total-label">Jobs</span>
                          </>
                        ) : (
                          <span className="jobs-region-total-unavailable">Data Unavailable</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="jobs-region-separator"></div>
                    
                    {/* Additional Metrics - Only show if not NA */}
                    <div className="jobs-region-metrics">
                      {isValidJobValue(regionData.women_in_percent) && (
                        <div className="jobs-region-metric-item">
                          <div className={`jobs-region-metric-icon jobs-region-metric-icon-${theme}`}>
                            <Icon iconName={ICONS.POPULATION} alt="Women in %" />
                          </div>
                          <div className="jobs-region-metric-content">
                            <span className="jobs-region-metric-label">Women in %</span>
                            <span className="jobs-region-metric-value">{Math.round(regionData.women_in_percent)}%</span>
                          </div>
                        </div>
                      )}
                      {isValidJobValue(regionData.youth_value) && (
                        <div className="jobs-region-metric-item">
                          <div className={`jobs-region-metric-icon jobs-region-metric-icon-${theme}`}>
                            <Icon iconName={ICONS.ENERGY_ACCESS} alt="Youth" />
                          </div>
                          <div className="jobs-region-metric-content">
                            <span className="jobs-region-metric-label">Youth</span>
                            <span className="jobs-region-metric-value">{Math.round(regionData.youth_value)}%</span>
                          </div>
                        </div>
                      )}
                      <div className="jobs-region-metric-item">
                        <div className={`jobs-region-metric-icon jobs-region-metric-icon-${theme}`}>
                          <Icon iconName={ICONS.ECONOMIC_IMPACT} alt="Business Headed by Women" />
                        </div>
                        <div className="jobs-region-metric-content">
                          <span className="jobs-region-metric-label">Business Headed by Women</span>
                          {isValidJobValue(regionData.women_smes_value) ? (
                            <span className="jobs-region-metric-value">{formatNumber(regionData.women_smes_value)}</span>
                          ) : (
                            <span className="jobs-region-category-unavailable">Data Unavailable</span>
                          )}
                        </div>
                      </div>
                      {isValidJobValue(regionData.permanent_value) && (
                        <div className="jobs-region-metric-item">
                          <div className={`jobs-region-metric-icon jobs-region-metric-icon-${theme}`}>
                            <Icon iconName={ICONS.ECONOMICS} alt="Permanent" />
                          </div>
                          <div className="jobs-region-metric-content">
                            <span className="jobs-region-metric-label">Permanent</span>
                            <span className="jobs-region-metric-value">{formatNumber(regionData.permanent_value)}</span>
                          </div>
                        </div>
                      )}
                      {isValidJobValue(regionData.temporary_value) && (
                        <div className="jobs-region-metric-item">
                          <div className={`jobs-region-metric-icon jobs-region-metric-icon-${theme}`}>
                            <Icon iconName={ICONS.ECONOMIC_IMPACT} alt="Temporary" />
                          </div>
                          <div className="jobs-region-metric-content">
                            <span className="jobs-region-metric-label">Temporary</span>
                            <span className="jobs-region-metric-value">{formatNumber(regionData.temporary_value)}</span>
                          </div>
                        </div>
                      )}
                      {isValidJobValue(regionData.direct_value) && (
                        <div className="jobs-region-metric-item">
                          <div className={`jobs-region-metric-icon jobs-region-metric-icon-${theme}`}>
                            <Icon iconName={ICONS.SMART_GRID} alt="Direct" />
                          </div>
                          <div className="jobs-region-metric-content">
                            <span className="jobs-region-metric-label">Direct</span>
                            <span className="jobs-region-metric-value">{formatNumber(regionData.direct_value)}</span>
                          </div>
                        </div>
                      )}
                      {isValidJobValue(regionData.indirect_value) && (
                        <div className="jobs-region-metric-item">
                          <div className={`jobs-region-metric-icon jobs-region-metric-icon-${theme}`}>
                            <Icon iconName={ICONS.POWER_GRID} alt="Indirect" />
                          </div>
                          <div className="jobs-region-metric-content">
                            <span className="jobs-region-metric-label">Indirect</span>
                            <span className="jobs-region-metric-value">{formatNumber(regionData.indirect_value)}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Job Categories Section */}
                    <div className="jobs-region-categories-header">
                      <span className="jobs-region-categories-title">Job Categories</span>
                    </div>
                    <div className="jobs-region-categories-separator"></div>
                    
                    <div className="jobs-region-categories">
                      {/* Construction - Always show */}
                      <div className="jobs-region-category-item">
                        <div className="jobs-region-category-icon">
                          <Icon iconName={ICONS.POWER_GRID} alt="Construction" />
                        </div>
                        <div className="jobs-region-category-content">
                          <span className="jobs-region-category-label">Construction</span>
                          {isValidJobValue(regionData.construction_value) ? (
                            <span className="jobs-region-category-value">{formatNumber(regionData.construction_value)}</span>
                          ) : (
                            <span className="jobs-region-category-unavailable">Data Unavailable</span>
                          )}
                        </div>
                      </div>

                      {/* Operational - Always show */}
                      <div className="jobs-region-category-item">
                        <div className="jobs-region-category-icon">
                          <Icon iconName={ICONS.SMART_GRID} alt="Operational" />
                        </div>
                        <div className="jobs-region-category-content">
                          <span className="jobs-region-category-label">Operational</span>
                          {isValidJobValue(regionData.operation_value) ? (
                            <span className="jobs-region-category-value">{formatNumber(regionData.operation_value)}</span>
                          ) : (
                            <span className="jobs-region-category-unavailable">Data Unavailable</span>
                          )}
                        </div>
                      </div>

                      {/* Technicians - Always show */}
                      <div className="jobs-region-category-item">
                        <div className="jobs-region-category-icon">
                          <Icon iconName={ICONS.SMART_GRID} alt="Technicians" />
                        </div>
                        <div className="jobs-region-category-content">
                          <span className="jobs-region-category-label">Technicians</span>
                          {isValidJobValue(regionData.technicians_value) ? (
                            <span className="jobs-region-category-value">{formatNumber(regionData.technicians_value)}</span>
                          ) : (
                            <span className="jobs-region-category-unavailable">Data Unavailable</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {/* Image Slider Card - Always visible */}
              <div className="jobs-region-card jobs-region-card-orange jobs-region-card-slider">
                <div className="jobs-region-slider-container">
                  <Slider
                    dots={true}
                    infinite={true}
                    speed={500}
                    slidesToShow={1}
                    slidesToScroll={1}
                    autoplay={true}
                    autoplaySpeed={3000}
                    arrows={false}
                    fade={true}
                    cssEase="linear"
                  >
                    <div className="jobs-region-slide">
                      <img src="/images/job-impacted-slider/slide-1.jpg" alt="Energy sector job creation and training programs" />
                    </div>
                    <div className="jobs-region-slide">
                      <img src="/images/job-impacted-slider/slide-2.jpg" alt="Renewable energy workforce development" />
                    </div>
                    <div className="jobs-region-slide">
                      <img src="/images/job-impacted-slider/slide-3.jpg" alt="Clean energy employment opportunities" />
                    </div>
                  </Slider>
                </div>
              </div>
            </div>
          ) : (
            <div className="jobs-regions-loading">
              <p>Please select a region to view jobs data</p>
            </div>
          )}
        </div>
      </section>

      {/* Impact Stories - Compact Design */}
      <section className="impact-stories-section">
        <div className="container">
          <div className="section-header">
            <h2 className="sub-section-title">Impact Stories</h2>
            <p className="section-subtitle">Real-world impact from our clean energy projects</p>
          </div>

          <div className="stories-compact-grid">
            {storiesLoading ? (
              // Loading state - show 3 placeholder cards
              Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="story-item-compact">
                  <div className="story-item-header">
                    <div className="story-item-icon"><Icon iconName={ICONS.ENERGY_ACCESS} alt="Loading" /></div>
                    <div className="story-item-title-group">
                      <h3 className="story-item-title">Loading...</h3>
                      <span className="story-item-location">—</span>
                    </div>
                  </div>
                  <p className="story-item-description">Loading impact story...</p>
                  <div className="story-item-footer">
                    <span className="story-item-metric">—</span>
                  </div>
                </div>
              ))
            ) : impactStories.length > 0 ? (
              // Show actual stories from API
              impactStories.map((story, index) => (
                <div key={index} className="story-item-compact">
                  <div className="story-item-header">
                    <div className="story-item-icon">
                      <Icon iconName={getStoryIcon(story.title || '', story.impact_story || '')} alt={story.title || 'Impact Story'} />
                    </div>
                    <div className="story-item-title-group">
                      <h3 className="story-item-title">{story.title || 'Impact Story'}</h3>
                      <span className="story-item-location">{story.country || '—'}</span>
                    </div>
                  </div>
                  <p className="story-item-description">
                    {formatImpactStory(story.impact_story) || 'No description available.'}
                  </p>
                  <div className="story-item-footer">
                    <span className="story-item-metric">
                      {formatStoryMetric(story.impact_value) || '—'}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              // No stories available - show static fallback or empty message
              <>
                <div className="story-item-compact">
                  <div className="story-item-header">
                    <div className="story-item-icon"><Icon iconName={ICONS.SCHOOL} alt="Lighting Rural Schools" /></div>
                    <div className="story-item-title-group">
                      <h3 className="story-item-title">Lighting Rural Schools</h3>
                      <span className="story-item-location">Kenya</span>
                    </div>
                  </div>
                  <p className="story-item-description">250 schools and 7,500 students now have access to reliable electricity for learning and digital education.</p>
                  <div className="story-item-footer">
                    <span className="story-item-metric">8,500 students</span>
                  </div>
                </div>

                <div className="story-item-compact">
                  <div className="story-item-header">
                    <div className="story-item-icon"><Icon iconName={ICONS.SOCIAL_IMPACT} alt="Empowering Women Entrepreneurs" /></div>
                    <div className="story-item-title-group">
                      <h3 className="story-item-title">Empowering Women Entrepreneurs</h3>
                      <span className="story-item-location">Tanzania</span>
                    </div>
                  </div>
                  <p className="story-item-description">500 women entrepreneurs across 10,000 villages now have access to clean energy for their businesses.</p>
                  <div className="story-item-footer">
                    <span className="story-item-metric">500 businesses</span>
                  </div>
                </div>

                <div className="story-item-compact">
                  <div className="story-item-header">
                    <div className="story-item-icon"><Icon iconName={ICONS.ENERGY_ACCESS} alt="Clean Water Access" /></div>
                    <div className="story-item-title-group">
                      <h3 className="story-item-title">Clean Water Access</h3>
                      <span className="story-item-location">Ethiopia</span>
                    </div>
                  </div>
                  <p className="story-item-description">100 solar-powered water pumps providing clean water access to 42,000 people in rural communities.</p>
                  <div className="story-item-footer">
                    <span className="story-item-metric">42,000 people</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

    </div>
    </>
  );
};

export default ImpactInsightsPage;
